import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
} from "reactstrap";
import DiscountService from "../../../Services/QRCodeServices/DiscountService/DiscountService";
import { MenuCategoryDropdown } from "./MenuCategoryDropdown";
import { MenuItemDropdown } from "./MenuItemDropdown";
import { useFormik } from "formik";
import DiscountSchema from "./DiscountSchema";

export const EditDiscount = ({
  isOpen,
  toggle,
  discountId,
  refreshDiscounts,
}) => {
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);

  const formik = useFormik({
    initialValues: {
      title: "",
      description: "",
      startDate: "",
      endDate: "",
      discountPercentage: "",
      isOverEntireMenu: false,
      categoryIds: [],
      itemIds: [],
    },
    validationSchema: DiscountSchema,
    onSubmit: async (values) => {
      try {
        // Construct the payload
        const payload = {
          ...values,
          startDate: new Date(values.startDate).toISOString(),
          endDate: new Date(values.endDate).toISOString(),
          categoryIds: values.categoryIds || [], // Ensure categoryIds is an array
          itemIds: values.itemIds || [], // Ensure itemIds is an array
        };

        console.log("Payload:", payload); // Log the payload for debugging

        // Send the update request
        await DiscountService.update(discountId, payload);

        // Close the modal and refresh the discounts list
        toggle();
        refreshDiscounts();
      } catch (error) {
        console.error("Error updating discount:", error);
      }
    },
  });

  useEffect(() => {
    if (isOpen && discountId) {
      fetchDiscountDetails(discountId);
    } else {
      // Reset form and state when modal is closed
      formik.resetForm();
      setSelectedCategories([]);
      setSelectedItems([]);
    }
  }, [isOpen, discountId]);

  const fetchDiscountDetails = async (id) => {
    try {
      const response = await DiscountService.detail(id);

      // Ensure unique categories and items
      const uniqueCategories = Array.from(
        new Set(response.category.map((cat) => cat.categoryId))
      ).map((categoryId) => {
        const category = response.category.find(
          (cat) => cat.categoryId === categoryId
        );
        return {
          value: category.categoryId,
          label: category.categoryName,
        };
      });

      const uniqueItems = Array.from(
        new Set(response.items.map((item) => item.itemId))
      ).map((itemId) => {
        const item = response.items.find((item) => item.itemId === itemId);
        return {
          value: item.itemId,
          label: item.itemName,
        };
      });

      // Set form values
      formik.setValues({
        title: response.title,
        description: response.description,
        startDate: response.startDate.split("T")[0], // Format date for the input field
        endDate: response.endDate.split("T")[0], // Format date for the input field
        discountPercentage: response.discountPercentage,
        isOverEntireMenu: response.isOverEntireMenu,
        categoryIds: uniqueCategories.map((cat) => cat.value), // Extract unique categoryIds
        itemIds: uniqueItems.map((item) => item.value), // Extract unique itemIds
      });

      // Set selected categories and items for the dropdowns
      setSelectedCategories(uniqueCategories);
      setSelectedItems(uniqueItems);
    } catch (error) {
      console.error("Error fetching discount details:", error);
    }
  };

  const handleCategoryChange = (selectedOptions) => {
    setSelectedCategories(selectedOptions);
    const categoryIds = selectedOptions.map((option) => option.value);
    formik.setFieldValue("categoryIds", categoryIds);
  };

  const handleItemChange = (selectedOptions) => {
    setSelectedItems(selectedOptions || []);
    const itemIds = selectedOptions.map((option) => option.value);
    formik.setFieldValue("itemIds", itemIds);
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle}>
      <ModalHeader toggle={toggle}>Edit Discount</ModalHeader>
      <ModalBody>
        <Form onSubmit={formik.handleSubmit}>
          <Row>
            <FormGroup>
              <Label for="title">Title</Label>
              <Input
                type="text"
                name="title"
                id="title"
                value={formik.values.title}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                invalid={formik.touched.title && !!formik.errors.title}
              />
              {formik.touched.title && formik.errors.title ? (
                <div className="text-danger">{formik.errors.title}</div>
              ) : null}
            </FormGroup>
          </Row>
          <Row>
            <FormGroup>
              <Label for="description">Description</Label>
              <Input
                type="text"
                name="description"
                id="description"
                value={formik.values.description}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                invalid={
                  formik.touched.description && !!formik.errors.description
                }
              />
              {formik.touched.description && formik.errors.description ? (
                <div className="text-danger">{formik.errors.description}</div>
              ) : null}
            </FormGroup>
          </Row>
          <Row>
            <Col>
              <FormGroup>
                <Label for="startDate">Start Date</Label>
                <Input
                  type="date"
                  name="startDate"
                  id="startDate"
                  value={formik.values.startDate}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={
                    formik.touched.startDate && !!formik.errors.startDate
                  }
                />
                {formik.touched.startDate && formik.errors.startDate ? (
                  <div className="text-danger">{formik.errors.startDate}</div>
                ) : null}
              </FormGroup>
            </Col>
            <Col>
              <FormGroup>
                <Label for="endDate">End Date</Label>
                <Input
                  type="date"
                  name="endDate"
                  id="endDate"
                  value={formik.values.endDate}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={formik.touched.endDate && !!formik.errors.endDate}
                />
                {formik.touched.endDate && formik.errors.endDate ? (
                  <div className="text-danger">{formik.errors.endDate}</div>
                ) : null}
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <FormGroup>
              <Label for="discountPercentage">Discount Percentage</Label>
              <Input
                type="number"
                name="discountPercentage"
                id="discountPercentage"
                value={formik.values.discountPercentage}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                invalid={
                  formik.touched.discountPercentage &&
                  !!formik.errors.discountPercentage
                }
              />
              {formik.touched.discountPercentage &&
              formik.errors.discountPercentage ? (
                <div className="text-danger">
                  {formik.errors.discountPercentage}
                </div>
              ) : null}
            </FormGroup>
          </Row>
          <Row>
            <FormGroup>
              <Label for="selectCategory">Category</Label>
              <MenuCategoryDropdown
                value={selectedCategories}
                onChange={handleCategoryChange}
              />
            </FormGroup>
          </Row>
          <Row>
            <FormGroup>
              <Label for="selectItem">Item</Label>
              <MenuItemDropdown
                value={selectedItems}
                onChange={handleItemChange}
              />
            </FormGroup>
          </Row>

          <FormGroup check>
            <Label check>
              <Input
                type="checkbox"
                name="isOverEntireMenu"
                checked={formik.values.isOverEntireMenu}
                onChange={formik.handleChange}
              />{" "}
              Apply Discount to Entire Menu
            </Label>
          </FormGroup>

          <ModalFooter>
            <Button type="submit" color="success">
              Save Changes
            </Button>{" "}
            <Button color="danger" onClick={toggle}>
              Cancel
            </Button>
          </ModalFooter>
        </Form>
      </ModalBody>
    </Modal>
  );
};
